import { createSlice } from '@reduxjs/toolkit'
import { getStateFromLocalStorage, storeStateAtLocalStorage } from '@src/reduxjs'

const initialState = {
  album: {
    id: null,
    category: {},
    name: null,
    dates: [],
  },
  search: {
    value: '',
    isOpen: false,
  },
  bib: {
    value: '',
    isOpen: false,
  },
  paymentMethod: {
    value: '',
    isOpen: false,
  },
  invoiceStatus: {
    value: '',
    isOpen: false,
  },
  status: {
    value: '',
    isOpen: false,
  },
  startDay: {
    value: null,
  },
  startTime: {
    value: null,
    options: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21],
  },
  startMinute: {
    value: null,
    options: [10, 20, 30, 40, 50],
  },
  face: {
    id: null,
    dataUrl: null,
  },
  image: {
    type: 'face',
    id: null,
    url: null,
  },
  users: {
    role: null,
    isOpen: false,
    options: ['admin', 'auditor', 'user', 'photographer', 'sales'],
  },
  labels: [],
  photographers: [],
  options: {
    selections: [],
    isOpen: false,
  },
  tags: {
    selections: [],
    isOpen: false,
  },
  colors: {
    selections: [],
    isOpen: false,
  },
  predictionNames: {
    selections: [],
    isOpen: false,
  },
  locals: {
    selections: [],
    isOpen: false,
  }
}

const filtersSlice = createSlice({
  name: 'filters',
  initialState: { ...initialState, ...getStateFromLocalStorage('filtersSlice') },
  reducers: {
    setAlbum: (state, action) => {
      state.startDay = initialState.startDay
      state.startMinute = initialState.startMinute
      state.startTime = initialState.startTime
      state.album = action.payload || initialState.album
    },
    setSearch: (state, action) => {
      if (!action.payload) {
        return { ...state, search: { ...state.search, value: initialState.search.value } }
      }

      if (action.payload.isOpen === false && state.search.isOpen === true) {
        return { ...state, search: initialState.search }
      }

      state.search = { ...state.search, ...action.payload }
    },
    setBib: (state, action) => {
      if (!action.payload) {
        return { ...state, bib: { ...state.bib, value: initialState.bib.value } }
      }

      if (action.payload.isOpen === false && state.bib.isOpen === true) {
        return { ...state, bib: initialState.bib }
      }

      state.bib = { ...state.bib, ...action.payload }
    },
    setPaymentMethod: (state, action) => {
      if (!action.payload) {
        return { ...state, paymentMethod: { ...state.paymentMethod, value: initialState.paymentMethod.value } }
      }

      if (action.payload.isOpen === false && state.paymentMethod.isOpen === true) {
        return { ...state, paymentMethod: initialState.paymentMethod }
      }

      state.paymentMethod = { ...state.paymentMethod, ...action.payload }
    },
    setInvoiceStatus: (state, action) => {
      if (!action.payload) {
        return { ...state, invoiceStatus: { ...state.invoiceStatus, value: initialState.invoiceStatus.value } }
      }

      if (action.payload.isOpen === false && state.invoiceStatus.isOpen === true) {
        return { ...state, invoiceStatus: initialState.invoiceStatus }
      }

      state.invoiceStatus = { ...state.invoiceStatus, ...action.payload }
    },
    setStatus: (state, action) => {
      if (!action.payload) {
        return { ...state, status: { ...state.status, value: initialState.status.value } }
      }

      if (action.payload.isOpen === false && state.status.isOpen === true) {
        return { ...state, status: initialState.status }
      }

      state.status = { ...state.status, ...action.payload }
    },
    setStartDay: (state, action) => {
      if (!action.payload || !action.payload.value) {
        state.startDay = initialState.startDay
        state.startTime.value = initialState.startTime.value
        state.startMinute.value = initialState.startMinute.value
        return state
      }
      state.startDay = action.payload
    },
    setStartTime: (state, action) => {
      if (!action.payload || !action.payload.value) {
        state.startTime = initialState.startTime
        state.startMinute = initialState.startMinute
        return state
      }
      if (action.payload.value === state.startTime.value) {
        state.startTime.value = initialState.startTime.value
        state.startMinute = initialState.startMinute
        return state
      }
      state.startTime.value = action.payload.value
    },
    setStartMinute: (state, action) => {
      if (!action.payload || !action.payload.value) {
        state.startMinute = initialState.startMinute
        return state
      }
      if (action.payload.value === state.startMinute.value) {
        state.startMinute.value = initialState.startMinute.value
        return state
      }
      state.startMinute.value = action.payload.value
    },
    setFace: (state, action) => {
      if (!action.payload || !action.payload.id) {
        return { ...state, face: initialState.face }
      }
      state.face = action.payload || initialState.face
    },
    setImage: (state, action) => {
      if (!action.payload || !action.payload.id) {
        return { ...state, image: initialState.image }
      }
      state.image = action.payload || initialState.image
    },
    setLabels: (state, action) => {
      state.labels = action.payload || initialState.labels
    },
    setPhotographers: (state, action) => {
      state.photographers = action.payload || initialState.photographers
    },
    setLocals: (state, action) => {
      if (!action.payload) {
        return { ...state, locals: { ...state.locals, selections: initialState.locals.selections } }
      }

      if (action.payload.isOpen === false && state.locals.isOpen === true) {
        return { ...state, locals: initialState.locals }
      }

      state.locals = { ...state.locals, ...action.payload }
    },
    toggleLocal: (state, action) => {
      if (state.locals.selections.find(({ id }) => id === action.payload.id)) {
        state.locals.selections = state.locals.selections.filter(({ id }) => id !== action.payload.id)
        return state
      }

      state.locals.selections.push(action.payload)

      return state
    },
    togglePhotographer: (state, action) => {
      if (state.photographers.find((i) => i.id === action.payload.id)) {
        state.photographers = state.photographers.filter((i) => i.id !== action.payload.id)
        return state
      }

      state.photographers.push(action.payload)

      return state
    },
    setColors: (state, action) => {
      if (!action.payload) {
        return { ...state, colors: { ...state.colors, selections: initialState.colors.selections } }
      }

      if (action.payload.isOpen === false && state.colors.isOpen === true) {
        return { ...state, colors: initialState.colors }
      }

      state.colors = { ...state.colors, ...action.payload }
    },
    setPredictionNames: (state, action) => {
      if (!action.payload) {
        return { ...state, predictionNames: { ...state.predictionNames, selections: initialState.predictionNames.selections } }
      }

      if (action.payload.isOpen === false && state.predictionNames.isOpen === true) {
        return { ...state, predictionNames: initialState.predictionNames }
      }

      state.predictionNames = { ...state.predictionNames, ...action.payload }
    },
    setOptions: (state, action) => {
      if (!action.payload) {
        return { ...state, options: { ...state.options, selections: initialState.options.selections } }
      }

      if (action.payload.isOpen === false && state.options.isOpen === true) {
        return { ...state, options: initialState.options }
      }

      state.options = { ...state.options, ...action.payload }
    },
    setTags: (state, action) => {
      if (!action.payload) {
        return { ...state, tags: { ...state.tags, selections: initialState.tags.selections } }
      }

      if (action.payload.isOpen === false && state.tags.isOpen === true) {
        return { ...state, tags: initialState.tags }
      }

      state.tags = { ...state.tags, ...action.payload }
    },
    setUsersRole: (state, action) => {
      if (!action.payload || state.users.role === action.payload.role) {
        return { ...state, users: { ...state.users, role: initialState.users.role } }
      }

      if ((action.payload.isOpen === false && state.users.isOpen === true)) {
        return { ...state, users: initialState.users }
      }

      state.users = { ...state.users, ...action.payload }
    },
    toggleColor: (state, action) => {
      if (state.colors.selections.find((i) => i === action.payload)) {
        state.colors.selections = state.colors.selections.filter((i) => i !== action.payload)

        return state
      }

      state.colors.selections.push(action.payload)

      return state
    },
    togglePredictionName: (state, action) => {
      if (state.predictionNames.selections.find((i) => i === action.payload)) {
        state.predictionNames.selections = state.predictionNames.selections.filter((i) => i !== action.payload)

        return state
      }

      state.predictionNames.selections.push(action.payload)

      return state
    },
    toggleOption: (state, action) => {
      if (action.payload === 'is_private') {
        state.options.selections = state.options.selections.filter((i) => i !== 'is_public')
      }

      if (action.payload === 'is_public') {
        state.options.selections = state.options.selections.filter((i) => i !== 'is_private')
      }

      if (state.options.selections.find((i) => i === action.payload)) {
        state.options.selections = state.options.selections.filter((i) => i !== action.payload)
        return state
      }

      state.options.selections.push(action.payload)

      return state
    },
    clearGroupAndSetLabel: (state, action) => {
      if (!action.payload || !action.payload.id) {
        return state
      }

      state.labels = [ ...state.labels.filter(({ group }) => group !== action.payload.group), action.payload ]

      return state
    },
    toggleLabel: (state, action) => {
      if (state.labels.find((i) => i.id === action.payload.id)) {
        state.labels = state.labels.filter((i) => i.id !== action.payload.id)
        return state
      }

      state.labels.push(action.payload)

      return state
    },
    clearGroup: (state, action) => {
      state.labels = state.labels.filter(({ group }) => group !== action.payload)
    },
  },
})

export const {
  clearGroup,
  setAlbum,
  setFace,
  setImage,
  setLabels,
  setLocals,
  setOptions,
  setTags,
  setPaymentMethod,
  setInvoiceStatus,
  setPhotographers,
  setSearch,
  setBib,
  setStartDay,
  setStartTime,
  setStartMinute,
  setStatus,
  setUsersRole,
  toggleLabel,
  clearGroupAndSetLabel,
  toggleLocal,
  toggleOption,
  toggleColor,
  togglePredictionName,
  setColors,
  setPredictionNames,
  togglePhotographer,
} = filtersSlice.actions

export default filtersSlice
